import { Box, IconButton, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import ApiService from "@/api/ApiService";
import { attribution, logistics_day, satellite_map } from "@/shared/maps/tiles";
import { useQuery } from "@tanstack/react-query";
import L from "leaflet";
import { useRef, useMemo, useEffect, useState, Fragment } from "react";
import { MapContainer, LayersControl, TileLayer, Marker } from "react-leaflet";
import jubau from "@/assets/icons/ss_ju.png";
import dittgen from "@/assets/icons/ss_dittgen.png";
import plant from "@/assets/icons/ss_plant.png";
import { useGlobalStore } from "@/stores/global";
import ManyToOneSelect from "@/shared/forms/ManyToOneSelect";
import dayjs from "dayjs";
import { GiftIcon } from "@mui-extra/icons/GiftIcon";
import { AttachFile, Clear, Phone, Search, Map as MapIcon, ViewList } from "@mui/icons-material";
import { ArmFlexIcon } from "@mui-extra/icons/ArmFlexIcon";
import { AccountHardHatIcon } from "@mui-extra/icons/AccountHardHatIcon";
import { DirectStoffstromEditor } from "./DirectStoffstromEditor";
import useRights from "@/shared/api/useRights";
import { TopBar } from "@/shared/structure/TopBar";
import { DirectStoffstromCreator } from "./DirectStoffstromCreator";
import { DataGridPro, GridColDef, useGridApiRef } from "@mui/x-data-grid-pro";
import { usePersistentDataGrid } from "@/shared/hooks/usePersistentDataGrid";

const getIcon = (region: string): L.Icon => {
  let iconUrl: string;
  let size: [number, number];
  let anchor;

  if (region.toLowerCase().includes("dittgen")) {
    iconUrl = dittgen;
    size = [56 * 0.7, 50 * 0.7];
  } else if (region.toLowerCase().includes("plant")) {
    iconUrl = plant;
    size = [56 * 0.7, 50 * 0.7];
  } else {
    iconUrl = jubau;
    size = [56 * 0.7, 50 * 0.7];
  }

  return L.icon({
    iconUrl,
    iconSize: size,
    iconAnchor: anchor || [size[0] / 2, size[1] / 2],
  });
};

const getEntrySource = (entry) => entry.project ?? entry.plant;

const isPlantEntry = (entry) => !!entry.plant;

const getEntryKey = (entry) => {
  if (entry.project) return `project_${entry.project.id}`;
  if (entry.plant) return `plant_${entry.plant.id}`;
  return null;
};

const getEntryRegion = (entry) => {
  if (entry.project) return entry.project.region.name;
  if (entry.plant) return "whatever_plant";
  return "";
};

const getPositionFromEntry = (entry) => {
  if (entry.plant) {
    return entry.plant.position?.site ?? null;
  }

  if (!entry.project?.position) return null;

  return !entry.project.position.useYard || entry.easyTarget !== "LAGER" || !entry.project.position.yard
    ? entry.project.position.site
    : entry.project.position.yard;
};

const getEntryNumber = (entry) => getEntrySource(entry)?.number ?? "";

const getEntryName = (entry) => getEntrySource(entry)?.name ?? "";

const getEntryPlannedStart = (entry) => entry.project?.plannedStart ?? entry.validFrom;

const getEntryPlannedEnd = (entry) => entry.project?.plannedEnd ?? entry.validTo;

const getEntryDateText = (entry) =>
  !!entry.date
    ? entry.date
    : "von " +
      dayjs(entry.validFrom || getEntryPlannedStart(entry)).format("DD.MM.YYYY") +
      " bis " +
      dayjs(entry.validTo || getEntryPlannedEnd(entry)).format("DD.MM.YYYY");

const getEntryDateSortValue = (entry) => dayjs(entry.date || entry.validFrom || getEntryPlannedStart(entry)).toDate();

const getAmountNumber = (amount) => {
  const parsed = parseFloat(String(amount).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
};

const formatAmount = (amount) => `${amount}${/[a-zA-Z]/.test(String(amount)) ? "" : " t"}`;

const getContactName = (entry) => {
  if (isPlantEntry(entry)) return entry.plant?.contact ?? "";

  const manager = entry.project?.manager;
  return manager ? `${manager.firstName} ${manager.name}` : "";
};

const getContactPhone = (entry) => {
  if (isPlantEntry(entry)) return entry.plant?.phone ?? "";
  return entry.project?.manager?.phone ?? "";
};

const getPolierName = (entry) => entry.project?.polier?.name ?? "";

const getPolierPhone = (entry) => entry.project?.polier?.phone ?? "";

function getStoffstromColumns(rights): GridColDef[] {
  return [
    {
      field: "type",
      headerName: "Typ",
      width: 120,
      display: "flex",
      type: "singleSelect",
      valueOptions: [
        { value: "OFFER", label: "Angebot" },
        { value: "REQUEST", label: "Gesuch" },
      ],
      valueFormatter: (value) => (value === "OFFER" ? "Angebot" : value === "REQUEST" ? "Gesuch" : ""),
      renderCell: (params) =>
        params.row.type === "OFFER" ? (
          <>
            <GiftIcon fontSize="small" sx={{ mr: "4px" }} />
            Angebot
          </>
        ) : (
          <>
            <Search fontSize="small" sx={{ mr: "4px" }} />
            Gesuch
          </>
        ),
    },
    {
      field: "category",
      headerName: "Kategorie",
      flex: 1,
      minWidth: 160,
      valueGetter: (_, row) => row.category?.name ?? "",
    },
    {
      field: "amount",
      headerName: "Menge",
      width: 110,
      type: "number",
      align: "right",
      headerAlign: "right",
      valueGetter: (_, row) => getAmountNumber(row.amount),
      valueFormatter: (_, row) => formatAmount(row.amount),
      renderCell: (params) => formatAmount(params.row.amount),
    },
    {
      field: "dateRange",
      headerName: "Zeitraum",
      width: 210,
      type: "date",
      valueGetter: (_, row) => getEntryDateSortValue(row),
      valueFormatter: (_, row) => getEntryDateText(row),
      renderCell: (params) => getEntryDateText(params.row),
    },
    {
      field: "location",
      headerName: "Ort",
      flex: 1,
      minWidth: 220,
      valueGetter: (_, row) => `${getEntryNumber(row)} ${getEntryName(row)}`,
      renderCell: (params) => (
        <>
          <span className="mmt_number">{getEntryNumber(params.row)}</span>
          {getEntryName(params.row)}
        </>
      ),
    },
    {
      field: "contact",
      headerName: "Ansprechpartner",
      flex: 1,
      minWidth: 190,
      display: "flex",
      valueGetter: (_, row) => getContactName(row),
      renderCell: (params) => {
        const name = getContactName(params.row);
        const phone = getContactPhone(params.row);

        return (
          <>
            {name}
            {!!phone ? (
              <Tooltip title={phone}>
                <IconButton
                  size="small"
                  component="a"
                  href={`tel:${phone}`}
                  onClick={(e) => e.stopPropagation()}
                  sx={{ ml: "4px", p: 0, color: "#444" }}
                >
                  <Phone fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </>
        );
      },
    },
    {
      field: "polier",
      headerName: "Polier",
      flex: 1,
      minWidth: 160,
      display: "flex",
      valueGetter: (_, row) => getPolierName(row),
      renderCell: (params) => {
        const name = getPolierName(params.row);
        const phone = getPolierPhone(params.row);

        return (
          <>
            {name}
            {!!phone ? (
              <Tooltip title={phone}>
                <IconButton
                  size="small"
                  component="a"
                  href={`tel:${phone}`}
                  onClick={(e) => e.stopPropagation()}
                  sx={{ ml: "4px", p: 0, color: "#444" }}
                >
                  <Phone fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </>
        );
      },
    },
    {
      field: "comment",
      headerName: "Kommentar",
      flex: 1,
      minWidth: 180,
      valueGetter: (_, row) => row.comment ?? "",
    },
    {
      field: "reservationComment",
      headerName: "Reservierung",
      flex: 1,
      minWidth: 180,
      valueGetter: (_, row) => row.reservationComment ?? "",
    },
    {
      field: "attachments",
      headerName: "Anhänge",
      width: 110,
      type: "number",
      valueGetter: (_, row) => row.attachments?.length ?? 0,
      renderCell: (params) =>
        params.row.attachments?.length > 0 ? (
          <Tooltip
            arrow
            placement="left"
            title={
              <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                {params.row.attachments.map((att) => (
                  <Box
                    key={att.filename}
                    component="a"
                    href={(import.meta.env.VITE_APP_URL + "/image/") + att.filename}
                    target="_blank"
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      color: "white",
                      textDecoration: "underline",
                      display: "block",
                    }}
                  >
                    {att.originalName}
                  </Box>
                ))}
              </Box>
            }
          >
            <span>
              <AttachFile fontSize="small" sx={{ verticalAlign: "middle" }} />
              {params.row.attachments.length}
            </span>
          </Tooltip>
        ) : (
          ""
        ),
    },
    {
      field: "createdAt",
      headerName: "Erstellt am",
      width: 170,
      type: "dateTime",
      valueGetter: (_, row) => dayjs.utc(row.createdAt).local().toDate(),
      valueFormatter: (_, row) => dayjs.utc(row.createdAt).local().format("DD.MM.YYYY HH:mm"),
    },
    {
      field: "edit",
      headerName: "",
      width: 80,
      display: "flex",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) =>
        "DISPOSITION" in rights ||
        "EDIT_ALL" in rights ||
        "STOFFSTROM" in rights ||
        ("PLANT" in rights && params.row.plant?.id === parseInt(rights["PLANT"])) ? (
          <DirectStoffstromEditor
            order={{ ...params.row, category: params.row.category.id }}
            plannedStart={getEntryPlannedStart(params.row)}
            plannedEnd={getEntryPlannedEnd(params.row)}
          />
        ) : null,
    },
  ];
}

export default function Stoffstrom({}) {
  const [filterCategory, setFilterCategory] = useState(null);
  const [filterCategoryBetter, setFilterCategoryBetter] = useState([]);
  const [filterType, setFilterType] = useState(null);
  const [currentlySelectedIds, setCurrentlySelectedIds] = useState(null);
  const [viewMode, setViewMode] = useState<"map" | "list">("map");

  const rights = useRights();
  const apiRef = useGridApiRef();

  const setPageTitle = useGlobalStore((x) => x.setPageTitle);

  useEffect(() => setPageTitle("Stoffstrom"), []);

  const hasFitted = useRef(false);
  const map = useRef();

  const { data, isLoading } = useQuery({
    queryKey: ["stoffstrom"],
    queryFn: ApiService.getStoffstromMap(),
  });

  const filteredData = useMemo(() => {
    if (!data) return [];

    return data.filter((s) =>
      !filterType && !filterCategory
        ? true
        : (!filterType || filterType === s.type) &&
          (!filterCategory || filterCategory === s.category.id || filterCategoryBetter.includes(s.category.id)),
    );
  }, [data, filterType, filterCategory, filterCategoryBetter]);

  const markers = useMemo(() => {
    if (!filteredData) return [];

    const grouped = {};

    for (let entry of filteredData) {
      const key = getEntryKey(entry);
      if (!key) continue;

      if (!(key in grouped)) {
        const position = getPositionFromEntry(entry);
        if (!position) continue;

        grouped[key] = {
          region: getEntryRegion(entry),
          id: key,
          entries: [],
          position,
        };
      }

      grouped[key].entries.push(entry.id);
    }

    return Object.values(grouped);
  }, [filteredData]);

  useEffect(() => {
    if (!map.current || hasFitted.current || markers.length === 0) return;

    const bounds = L.latLngBounds(markers.map((m) => m.position));
    map.current.fitBounds(bounds, { paddingBottomRight: [0, 0] });
    hasFitted.current = true;
  }, [markers]);

  const currentlySelected = !currentlySelectedIds ? null : data?.filter((e) => currentlySelectedIds.includes(e.id));

  useEffect(() => {
    if (!currentlySelected || currentlySelected.length === 0 || !map.current) return;

    const position = getPositionFromEntry(currentlySelected[0]);
    if (!position) return;

    map.current.flyTo(position, Math.max(map.current.getZoom(), 14), {
      animate: true,
    });
  }, [currentlySelected]);

  const columns = useMemo(() => getStoffstromColumns(rights), [rights]);
  const { gridProps } = usePersistentDataGrid("stoffstrom", columns);

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        p: 0,
        background: "#eee",
      }}
    >
      <TopBar>
        <DirectStoffstromCreator plannedStart={dayjs().startOf("day").format("YYYY-MM-DD")} plannedEnd={dayjs().endOf("year").format("YYYY-MM-DD")} />

        <ToggleButtonGroup
          size="small"
          exclusive
          value={viewMode}
          onChange={(_, val) => {
            if (!!val) setViewMode(val);
          }}
        >
          <ToggleButton value="map">
            <MapIcon sx={{ mr: 1 }} />
            Karte
          </ToggleButton>
          <ToggleButton value="list">
            <ViewList sx={{ mr: 1 }} />
            Liste
          </ToggleButton>
        </ToggleButtonGroup>
      </TopBar>

      {viewMode === "list" ? (
        <Box sx={{ flex: 1, overflow: "hidden", m: 1 }}>
          <DataGridPro
            rows={filteredData}
            columns={columns}
            apiRef={apiRef}
            showToolbar
            rowHeight={32}
            getRowId={(row) => row.id}
            loading={isLoading}
            onRowClick={(params) => setCurrentlySelectedIds([params.row.id])}
            {...gridProps}
          />
        </Box>
      ) : (
        <Box display="flex" flex={1} overflow={"hidden"}>
          <Box flex={1}>
            <MapContainer ref={map} center={[49.7668, 7.3393]} zoom={11} style={{ height: "100%", width: "100%" }}>
              <LayersControl position="bottomleft">
                <LayersControl.BaseLayer checked name="Straßenkarte">
                  <TileLayer attribution={attribution} url={logistics_day} />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name="Satellit">
                  <TileLayer attribution={attribution} url={satellite_map} />
                </LayersControl.BaseLayer>
              </LayersControl>

              {markers.map((marker) => {
                const icon = getIcon(marker.region);

                return (
                  <Marker
                    key={marker.id}
                    position={marker.position}
                    icon={icon}
                    eventHandlers={{
                      click: () => {
                        setCurrentlySelectedIds(marker.entries);
                      },
                    }}
                  />
                );
              })}
            </MapContainer>
          </Box>

          <Box
            sx={{
              flex: 1,
              maxWidth: "350px",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              bgcolor: "background.paper",
              borderLeft: "1px solid #aaa",
              boxShadow: 3,
              overflowY: "hidden",
            }}
          >
            {!currentlySelected || currentlySelected.length === 0 ? (
              <Fragment>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "white",
                    borderBottom: "1px solid #aaa",
                  }}
                >
                  <ToggleButtonGroup
                    orientation="horizontal"
                    exclusive
                    fullWidth
                    value={filterType}
                    onChange={(_, val) => setFilterType(val || null)}
                  >
                    <ToggleButton value="OFFER">
                      <GiftIcon sx={{ mr: 1 }} /> Angebote
                    </ToggleButton>
                    <ToggleButton value="REQUEST">
                      <Search sx={{ mr: 1 }} /> Gesuche
                    </ToggleButton>
                  </ToggleButtonGroup>

                  <ManyToOneSelect
                    remoteFilterReady={true}
                    label="Kategorie"
                    placeholder="Bitte wählen..."
                    entity={"stoffstromCategory"}
                    currentId={filterCategory ?? 0}
                    sideEffect={(x) => setFilterCategoryBetter(!!x ? x.betterCategories : [])}
                    setId={(nid) => setFilterCategory(nid || null)}
                    disabled={false}
                    sideEffectOnlyOnChange={false}
                  />
                </Box>

                <Box
                  sx={{
                    overflow: "auto",
                    backgroundColor: "#fafafa",
                    display: "flex",
                    flexDirection: "column",
                    padding: "4px",
                    pt: "8px",
                    flex: 1,
                  }}
                >
                  {filteredData.map((y) => (
                    <Box
                      key={y.id}
                      sx={{
                        background: "white",
                        cursor: "pointer",
                        "&:hover": {
                          opacity: 0.5,
                        },
                        display: "flex",
                        flexDirection: "column",
                        border: "1px solid #777",
                        mb: "8px",
                        mx: "4px",
                        alignItems: "stretch",
                        overflow: "hidden",
                        borderRadius: "5px",
                        flexShrink: 0,
                        fontSize: "13px",
                      }}
                      onClick={() => setCurrentlySelectedIds([y.id])}
                    >
                      <Box sx={{ display: "flex", borderBottom: "1px solid #777" }}>
                        <Box
                          sx={{
                            borderRight: "1px solid #aaa",
                            color: "#333",
                            boxShadow: "inset -4px 0px 5px rgba(0,0,0,0.1)",
                            p: "4px 6px",
                            lineHeight: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                          style={y.type === "OFFER" ? { background: "#dcfbd4" } : { background: "rgb(251, 212, 243)" }}
                        >
                          {y.type === "OFFER" ? <GiftIcon fontSize="small" /> : <Search fontSize="small" />}
                        </Box>

                        <Box
                          sx={{
                            flex: 1,
                            p: "4px",
                            overflow: "hidden",
                            lineHeight: 1,
                            fontWeight: "bold",
                            alignSelf: "center",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {y.category.name}
                        </Box>

                        <Box
                          sx={{
                            borderLeft: "1px solid #aaa",
                            color: "#333",
                            boxShadow: "inset -4px 0px 5px rgba(0,0,0,0.1)",
                            p: "4px 6px",
                            lineHeight: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                          style={y.type === "OFFER" ? { background: "#dcfbd4" } : { background: "rgb(251, 212, 243)" }}
                        >
                          {y.amount}
                          {/[a-zA-Z]/.test(y.amount) ? "" : " t"}
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          lineHeight: 1,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          p: "3px",
                          backgroundColor: "#eee",
                        }}
                      >
                        {getEntryDateText(y)}
                      </Box>

                      <Box
                        sx={{
                          p: "2px",
                          pt: "3px",
                          borderTop: "1px solid #aaa",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          fontSize: "12px",
                          lineHeight: 1.1,
                          textAlign: "right",
                          backgroundColor: "#f7f7f7",
                          boxShadow: "0 2px 3px rgba(0,0,0,0.1) inset",
                        }}
                      >
                        <span style={{ fontSize: "10px", marginRight: "2px" }}>{getEntryNumber(y)}</span>
                        {getEntryName(y)}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Fragment>
            ) : (
              <Fragment>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "white",
                    borderBottom: "1px solid #aaa",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ fontSize: "16px", fontWeight: "bold" }}>
                      <span
                        style={{
                          fontSize: "12px",
                          opacity: 0.8,
                          marginRight: "4px",
                        }}
                      >
                        {getEntryNumber(currentlySelected[0])}
                      </span>
                      {getEntryName(currentlySelected[0])}
                    </Box>

                    <Box>
                      <IconButton
                        onClick={() => {
                          hasFitted.current = false;
                          setCurrentlySelectedIds(null);
                        }}
                        size="small"
                      >
                        <Clear />
                      </IconButton>
                    </Box>
                  </Box>

                  {isPlantEntry(currentlySelected[0]) && !!currentlySelected[0].plant.contact ? (
                    <Box>
                      <AccountHardHatIcon sx={{ mr: "4px", verticalAlign: "text-top" }} fontSize="small" />
                      {currentlySelected[0].plant.contact}
                      {!!currentlySelected[0].plant.phone ? (
                        <span
                          style={{
                            fontSize: "12px",
                            opacity: 0.8,
                            marginLeft: "4px",
                          }}
                        >
                          {currentlySelected[0].plant.phone}
                        </span>
                      ) : null}
                    </Box>
                  ) : null}

                  {!isPlantEntry(currentlySelected[0]) && !!currentlySelected[0].project.manager ? (
                    <Box>
                      <AccountHardHatIcon sx={{ mr: "4px", verticalAlign: "text-top" }} fontSize="small" />
                      {currentlySelected[0].project.manager.firstName} {currentlySelected[0].project.manager.name}
                      {currentlySelected[0].project.manager.phone.length > 0 ? (
                        <span
                          style={{
                            fontSize: "12px",
                            opacity: 0.8,
                            marginLeft: "4px",
                          }}
                        >
                          {currentlySelected[0].project.manager.phone}
                        </span>
                      ) : null}
                    </Box>
                  ) : null}

                  {!isPlantEntry(currentlySelected[0]) && !!currentlySelected[0].project.polier ? (
                    <Box>
                      <ArmFlexIcon sx={{ mr: "4px", verticalAlign: "text-top" }} fontSize="small" />
                      {currentlySelected[0].project.polier.name}
                      {currentlySelected[0].project.polier.phone.length > 0 ? (
                        <span
                          style={{
                            fontSize: "12px",
                            opacity: 0.8,
                            marginLeft: "4px",
                          }}
                        >
                          {currentlySelected[0].project.polier.phone}
                        </span>
                      ) : null}
                    </Box>
                  ) : null}
                </Box>

                <Box
                  sx={{
                    overflow: "auto",
                    backgroundColor: "#fafafa",
                    display: "flex",
                    flexDirection: "column",
                    padding: "4px",
                    pt: "8px",
                    flex: 1,
                  }}
                >
                  {currentlySelected.map((y) => (
                    <Box
                      key={y.id}
                      sx={{
                        background: "white",
                        display: "flex",
                        flexDirection: "column",
                        border: "1px solid #777",
                        mb: "8px",
                        mx: "4px",
                        alignItems: "stretch",
                        overflow: "hidden",
                        borderRadius: "5px",
                        flexShrink: 0,
                        fontSize: "13px",
                      }}
                    >
                      <Box sx={{ display: "flex", borderBottom: "1px solid #777" }}>
                        <Box
                          sx={{
                            borderRight: "1px solid #aaa",
                            color: "#333",
                            boxShadow: "inset -4px 0px 5px rgba(0,0,0,0.1)",
                            p: "4px 6px",
                            lineHeight: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                          style={y.type === "OFFER" ? { background: "#dcfbd4" } : { background: "rgb(251, 212, 243)" }}
                        >
                          {y.type === "OFFER" ? <GiftIcon fontSize="small" /> : <Search fontSize="small" />}
                        </Box>

                        <Box
                          sx={{
                            flex: 1,
                            p: "4px",
                            overflow: "hidden",
                            lineHeight: 1,
                            fontWeight: "bold",
                            alignSelf: "center",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {y.category.name}
                        </Box>

                        <Box
                          sx={{
                            borderLeft: "1px solid #aaa",
                            color: "#333",
                            boxShadow: "inset -4px 0px 5px rgba(0,0,0,0.1)",
                            p: "4px 6px",
                            lineHeight: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                          style={y.type === "OFFER" ? { background: "#dcfbd4" } : { background: "rgb(251, 212, 243)" }}
                        >
                          {y.amount}
                          {/[a-zA-Z]/.test(y.amount) ? "" : " t"}
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          lineHeight: 1,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          p: "3px",
                          backgroundColor: "#eee",
                        }}
                      >
                        {getEntryDateText(y)}
                      </Box>

                      {!!y.comment ? (
                        <Box
                          sx={{
                            p: "2px",
                            pt: "3px",
                            borderTop: "1px solid #aaa",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            fontSize: "12px",
                            lineHeight: 1.1,
                            backgroundColor: "#f7f7f7",
                            boxShadow: "0 2px 3px rgba(0,0,0,0.1) inset",
                          }}
                        >
                          {y.comment}
                        </Box>
                      ) : null}

                      {!!y.reservationComment ? (
                        <Box
                          sx={{
                            p: "2px",
                            pt: "3px",
                            borderTop: "1px solid #aaa",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            fontSize: "12px",
                            lineHeight: 1.1,
                            backgroundColor: "#f7f7f7",
                            boxShadow: "0 2px 3px rgba(0,0,0,0.1) inset",
                          }}
                        >
                          {y.reservationComment}
                        </Box>
                      ) : null}

                      {y.attachments.map((att) => (
                        <Box
                          key={att.filename}
                          component="a"
                          href={(import.meta.env.VITE_APP_URL + "/image/") + att.filename}
                          target="_blank"
                          sx={{
                            p: "2px",
                            pt: "3px",
                            borderTop: "1px solid #aaa",
                            cursor: "pointer",
                            "&:hover": {
                              opacity: 0.5,
                            },
                            color: "#444",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            fontSize: "12px",
                            lineHeight: 1.1,
                            backgroundColor: "#f7f7f7",
                            boxShadow: "0 2px 3px rgba(0,0,0,0.1) inset",
                          }}
                        >
                          <AttachFile sx={{ mr: "2px", verticalAlign: "text-top" }} fontSize="inherit" />
                          {att.originalName}
                        </Box>
                      ))}

                      <Box
                        sx={{
                          p: "2px",
                          pt: "3px",
                          borderTop: "1px solid #aaa",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          fontSize: "12px",
                          lineHeight: 1.1,
                          backgroundColor: "#f7f7f7",
                          boxShadow: "0 2px 3px rgba(0,0,0,0.1) inset",
                        }}
                      >
                        Erstellt am {dayjs.utc(y.createdAt).local().format("DD.MM.YYYY [um] HH:mm [Uhr]")}
                      </Box>

                      {"DISPOSITION" in rights ||
                      "EDIT_ALL" in rights ||
                      "STOFFSTROM" in rights ||
                      ("PLANT" in rights && y.plant?.id === parseInt(rights["PLANT"])) ? (
                        <Box
                          sx={{
                            p: "2px",
                            pt: "3px",
                            borderTop: "1px solid #aaa",
                            justifyContent: "flex-end",
                            display: "flex",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            fontSize: "12px",
                            lineHeight: 1.1,
                            backgroundColor: "#f7f7f7",
                            boxShadow: "0 2px 3px rgba(0,0,0,0.1) inset",
                          }}
                        >
                          <DirectStoffstromEditor
                            order={{ ...y, category: y.category.id }}
                            plannedStart={getEntryPlannedStart(y)}
                            plannedEnd={getEntryPlannedEnd(y)}
                          />
                        </Box>
                      ) : null}
                    </Box>
                  ))}
                </Box>
              </Fragment>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}
